/** @jsx jsx */

import { jsx, css } from '@emotion/core';
import TextField, { TextFieldProps } from './TextField';

import { Theme } from 'features/Theme';
import { FieldProps } from 'formik';
import { useCallback, ChangeEvent, FocusEvent } from 'react';
import React from 'react';
import { path } from 'ramda';

const styles = {
  pristineAndEmpty: (theme: Theme) =>
    css`
      &.pristineAndEmpty .MuiInputBase-root {
        border-color: ${theme.mui.palette.text.secondary};
        border-width: 2px;
      }
    `,
};

/**
 * TextField with integration with Formik field props.
 * Automatically
 * - Connect with Formik's field value and callbacks
 * - set the errors attribute and display error messages
 * - set distinctive border for pristine fields without value.
 *
 * Example:
 *
 * ```
 * <Field name="fieldName">
 *   {fieldProps => <FormTextField {...fieldProps} label='Field name' />}
 * </Field>
 * ```
 */
export type FormTextFieldProps = TextFieldProps & {
  fieldProps: FieldProps;
};

export const FormTextField: React.ForwardRefExoticComponent<
  FormTextFieldProps
> = React.forwardRef((props, ref) => {
  const {
    fieldProps: {
      form,
      field: { onChange: formikOnChange, onBlur: formikOnBlur, ...field },
    },
    disabled,
    helperText,
    error,
    onChange,
    onBlur,
    ...rest
  } = props;

  const getNestedField = path(field.name.split('.'));
  const touched = getNestedField(form.touched);
  const formError = getNestedField(form.errors) as string | undefined;
  const hasError = error || Boolean(touched && formError);
  const pristineAndEmpty = Boolean(!disabled && !touched && !field.value);
  const newOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      formikOnChange(e);
      onChange && onChange(e);
    },
    [formikOnChange, onChange],
  );

  const newOnBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      formikOnBlur(e);
      onBlur && onBlur(e);
    },
    [formikOnBlur, onBlur],
  );

  return (
    <TextField
      {...field}
      ref={ref}
      onChange={newOnChange}
      onBlur={newOnBlur}
      error={hasError}
      disabled={disabled}
      {...rest}
      helperText={(hasError && formError) || helperText}
      css={styles.pristineAndEmpty}
      className={pristineAndEmpty ? 'pristineAndEmpty' : ''}
    />
  );
});

export default FormTextField;
